/**
 * DR-56 v0 (ghane docs/dev-requests/dr-56-search-landing-v2.md): who sees the new city landing,
 * and the origin every one of its links carries.
 *
 * The split is a hash of `terminal_id`, decided in the browser only. The HTML of /s/* is shared by
 * the CDN, so a server-side split would hand one visitor's arm to everyone; visitors without a
 * terminal yet (first visit, crawlers) and the app are left out and keep today's page.
 */

/** Must match the `argIn.ref` entry of `semantic-core` in semantic-search rules.json (!249). */
export const LANDING_REF = 'search_landing';

/** `preview` = the owner's look before launch (cookie `dr56_preview=1`); outside both arms. */
export type LandingArm = 'v0' | 'control' | 'preview';

/**
 * The experiment runs on the main site only. A partner's own site (white-label, `partnerKey`) limits search to
 * that centre, and these blocks search the whole platform; its theme only reaches the browser after hydration,
 * too late for the pre-paint script, so the host decides. `localhost` keeps local checks possible.
 */
export const LANDING_HOSTS = ['www.paziresh24.com', 'localhost'];
export const isLandingHost = (host: unknown): boolean => LANDING_HOSTS.includes(String(host ?? ''));

/** The preview cookie; only the exact value 1 counts. */
export const LANDING_PREVIEW_COOKIE = 'dr56_preview';
export const isLandingPreview = (cookie: unknown): boolean => cookie === '1';
/**
 * A link a colleague can just open (owner, 2026-10-29: setting a cookie by hand was too hard):
 * `?dr56=1` turns the preview on in that browser for 30 days, `?dr56=0` turns it off.
 */
export const LANDING_PREVIEW_PARAM = 'dr56';
export const LANDING_PREVIEW_DAYS = 30;
export const previewCookieFor = (search: string): string | null => {
  const m = search.match(new RegExp(`[?&]${LANDING_PREVIEW_PARAM}=([01])(?:&|$)`));
  if (!m) return null;
  return m[1] === '1'
    ? `${LANDING_PREVIEW_COOKIE}=1; path=/; max-age=${LANDING_PREVIEW_DAYS * 86400}; samesite=lax`
    : `${LANDING_PREVIEW_COOKIE}=; path=/; max-age=0; samesite=lax`;
};
export const applyPreviewParam = () => {
  if (typeof window === 'undefined') return;
  [previewCookieFor(window.location.search), ctaCookieFor(window.location.search)].forEach(cookie => {
    if (cookie) document.cookie = cookie;
  });
};

/** `SEARCH_LANDING_V0_PERCENT`: an integer share 0..100; anything else is 0 (off). */
export const parseLandingPercent = (raw: unknown): number => {
  const n = Number.parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 100);
};

const fnv1a32 = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/** 0..99, salted so this split is independent of other experiments keyed on the same id. */
export const landingBucket = (terminalId: string): number => fnv1a32(`dr56:${terminalId}`) % 100;

/**
 * A second split inside the new landing (owner, 2026-10-30): are filled card buttons a pull to click, or, with several
 * on screen at once, a load that confuses? Half the new-landing visitors get every card button hollow. Its own salt,
 * so it is independent of the landing split; the style rides on every event (`cta`) and the report compares the two.
 */
export type CtaStyle = 'filled' | 'outline';
export const CTA_COOKIE = 'dr56_cta';
export const CTA_PARAM = 'dr56cta';
/**
 * MurmurHash3's finaliser: spreads every input bit over the output. FNV-1a's low bit is only the parity of the input,
 * so `fnv % 2` followed the landing bucket (`% 100`, even) and the two halves were balanced only at even shares
 * (review 2026-10-30); the mixed top bit is not.
 */
const fmix32 = (h: number): number => {
  let x = h >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return x >>> 0;
};
export const ctaStyle = ({
  terminalId,
  override,
  preview = false,
}: {
  terminalId?: string | null;
  override?: unknown;
  /** The look cookie counts only in the preview: a measured visitor keeps the terminal's half. */
  preview?: boolean;
}): CtaStyle => {
  if (preview && (override === 'filled' || override === 'outline')) return override;
  if (!terminalId) return 'filled';
  return fmix32(fnv1a32(`dr56cta:${terminalId}`)) >>> 31 ? 'outline' : 'filled';
};
/** `?dr56cta=outline|filled` shows that style in this browser (30 days), for a look; `?dr56cta=0` drops it. */
export const ctaCookieFor = (search: string): string | null => {
  const m = search.match(new RegExp(`[?&]${CTA_PARAM}=(outline|filled|0)(?:&|$)`));
  if (!m) return null;
  return m[1] === '0'
    ? `${CTA_COOKIE}=; path=/; max-age=0; samesite=lax`
    : `${CTA_COOKIE}=${m[1]}; path=/; max-age=${LANDING_PREVIEW_DAYS * 86400}; samesite=lax`;
};

/** `null` = not in the experiment at all (no `landing_view` either). */
export const landingVariant = ({
  terminalId,
  percent,
  isApp,
  preview = false,
}: {
  terminalId?: string | null;
  percent: number;
  isApp: boolean;
  preview?: boolean;
}): LandingArm | null => {
  // Shown regardless of the share, and tagged `preview` so the report counts it in neither arm.
  if (preview) return 'preview';
  if (isApp || !terminalId) return null;
  return landingBucket(terminalId) < percent ? 'v0' : 'control';
};

/** A landing link: the target plus `ref`, block (`lb`) and position (`lp`), empty params dropped. */
export const landingUrl = (path: string, params: Record<string, string | undefined>, block: string, position?: number): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, value);
  });
  query.set('ref', LANDING_REF);
  query.set('lb', block);
  if (position) query.set('lp', String(position));
  return `${path}?${query.toString()}`;
};

/**
 * Which landing an address is (`template` of every event). The city landings (`/s/<city>/`, ±`?turn_type=consult`) are
 * the API's `is_landing`. The nationwide ones (owner, 2026-10-31: «میخوام ساخته بشه»): `/s/` (and `/s/ir/`, which the
 * server sends to `/s/`) is a list of every doctor in the API, never `is_landing`, so its address decides; online
 * (`/s/?turn_type=consult`) is the API's landing as in a city. A visitor with a saved city (or «اطراف من») is sent on
 * from `/s/` to that city by the page itself (`[[...params]].tsx`), so the nationwide landing is never theirs.
 */
export type LandingTemplate = 'presence_city' | 'consult_city' | 'general_ir' | 'consult_ir';
/** Query keys that change nothing `/s/` shows: the landing's own origin, the preview links, campaign tags. */
const NEUTRAL_KEYS = [
  'ref',
  'lb',
  'lp',
  LANDING_PREVIEW_PARAM,
  CTA_PARAM,
  // click ids and Google's own link tags (`srsltid` rides on many organic results)
  'gclid',
  'fbclid',
  'yclid',
  'msclkid',
  'srsltid',
  'gad_source',
  'gbraid',
  'wbraid',
  '_gl',
];
export const isNationalTemplate = (template: unknown) => template === 'general_ir' || template === 'consult_ir';
export const landingTemplate = ({
  params,
  query,
  isLanding,
  savedCity = false,
}: {
  params?: unknown;
  query: Record<string, unknown>;
  isLanding: boolean;
  /** Only known in the browser (cookie `new-city`, the store); the server render passes nothing. */
  savedCity?: boolean;
}): LandingTemplate | null => {
  const slugs = (Array.isArray(params) ? params : params ? [params] : []).map(String).filter(Boolean);
  const consult = query.turn_type === 'consult';
  if (slugs.length > 1) return null;
  if (slugs.length === 1 && slugs[0] !== 'ir') return isLanding ? (consult ? 'consult_city' : 'presence_city') : null;
  if (savedCity) return null;
  if (consult) return isLanding ? 'consult_ir' : null;
  const other = Object.keys(query).filter(key => !NEUTRAL_KEYS.includes(key) && !key.startsWith('utm_'));
  return other.length ? null : 'general_ir';
};
